/* ==========================================================================
   Opt-in popup — goal → email → SMS → done.

   Paired with snippets/optin-popup.liquid, which renders the markup and puts the two
   per-render values on #olsop-root as data-popup-id / data-open-delay.

   The flow's SHAPE came from a popup Sam brought over from another brand. Every
   integration in it is Organic Life Start's, lifted from welcome-popup.js so
   the two behave identically where it matters:

     · the ols-welcome-backend worker, /validate-email and /welcome-sms
     · Cloudflare Turnstile, invisible, lazy-loaded on first open
     · the worker's typo-suggestion response ("did you mean …@gmail.com?")
     · funnel events to Klaviyo, GA and Meta
     · the shared session/submitted keys and the __kla_id cookie check

   Written unminified on purpose. welcome-popup.js is minified output from a
   build that no longer exists in this repo, which makes it effectively
   unmaintainable; there is no build step here (see CLAUDE.md), so this ships
   as the source it is.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('olsop-root');
  if (!root) return;

  /* Config. The two per-render values ride on the root element as data attributes
     (data-popup-id, data-open-delay — set by snippets/optin-popup.liquid); the rest is
     shared with welcome-popup.js on purpose: one backend, one Turnstile key, one set of
     session/submitted keys, so the two popups cannot both fire at a visitor or
     double-subscribe them. Was an inline <script> setting window.OLSOP_CONFIG. */
  var CFG = {
    popupId:      root.getAttribute('data-popup-id') || 'POPUP_optin',
    openDelay:    parseInt(root.getAttribute('data-open-delay'), 10) || 5000,
    worker:       'https://ols-welcome-backend.ols-api.workers.dev',
    turnstileKey: '0x4AAAAAADSM5IbxYMBNQbf3',
    sessionKey:   'ols-welcome-shown',
    submittedKey: 'ols-welcome-submitted',
    emailCode:    'hi5'
  };

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var state = { goal: '', email: '', phone: '', sms: false };

  var q = function (s) { return root.querySelector(s); };
  var qa = function (s) { return Array.prototype.slice.call(root.querySelectorAll(s)); };

  /* ------------------------------------------------------------- tracking */
  /* Same three destinations and the same event names welcome-popup uses, so
     the funnel reports on one set of events no matter which popup a visitor
     saw. Each is wrapped: an ad blocker killing fbq must not stop the signup. */
  /* THE GOAL IS SAVED ON THE KLAVIYO PROFILE HERE, AND ONLY HERE (11 Sep 2026).

     Sam asked whether the popup's "where is your little one at" answer reaches
     Shopify or Klaviyo. Traced end to end, it reached neither:
       · the worker's /validate-email reads the email and the Turnstile token
         and nothing else, so the goal sent alongside them was discarded
       · /welcome-sms writes a Klaviyo profile, but with the SMS code and
         channel only, and the popup never sends it the goal
       · the worker's only Shopify call creates a discount code
       · the goal did ride on the track() events below, but an event is not
         the person: nothing ever wrote it onto the profile, which is what a
         segment or a flow split reads

     identify attaches it to the person instead of to an event, as the profile
     property `signup_goal`, which is what a segment or a flow split can read.
     It runs once the email has passed validation, so the address is real, and
     BEFORE the Email Submitted track so that event lands on the same profile.

     The queue is created if klaviyo.js has not arrived yet: it replays
     _learnq when it loads, where the existence check track() uses would simply
     drop the call. identify sets properties only; it grants no marketing
     consent. */
  function identify(props) {
    var p = { $email: state.email };
    Object.keys(props || {}).forEach(function (k) { if (props[k]) p[k] = props[k]; });
    try { (window._learnq = window._learnq || []).push(['identify', p]); } catch (e) {}
  }

  function track(event, props) {
    props = props || {};
    try { if (window._learnq) window._learnq.push(['track', event, props]); } catch (e) {}
    try { if (window.gtag) window.gtag('event', event, props); } catch (e) {}
    try { if (window.fbq) window.fbq('trackCustom', event, props); } catch (e) {}
  }

  /* ------------------------------------------------------------- gating */
  var forced = /[?&]welcome=1/.test(window.location.search);

  function alreadyKnown() {
    /* Klaviyo drops __kla_id once it has identified someone. If there is an
       email in it, this visitor is already on the list and does not need the
       offer. Copied from welcome-popup.js — same cookie, same decode. */
    if (document.cookie.indexOf('__kla_id') === -1) return false;
    try {
      var m = document.cookie.match(/__kla_id=([^;]+)/);
      if (!m) return false;
      var payload = JSON.parse(atob(decodeURIComponent(m[1]).split('.')[0]));
      return !!(payload && payload.$email);
    } catch (e) { return false; }
  }

  if (!forced) {
    try { if (localStorage.getItem(CFG.submittedKey)) return; } catch (e) {}
    try { if (sessionStorage.getItem(CFG.sessionKey)) return; } catch (e) {}
    if (alreadyKnown()) return;
  }

  /* ------------------------------------------------------------- css, lazily
     The stylesheet is requested only now, once the popup is going to open — a
     suppressed visitor never fetches it (mirrors welcome-popup). #olsop-root stays
     `hidden` until the sheet has loaded (open() waits, with a deadline), so the
     bare form never flashes. The href rides on the root as data-css. */
  var cssLink = null;
  function loadCss() {
    if (cssLink) return;
    var href = root.getAttribute('data-css');
    if (!href) return;
    cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.href = href;
    cssLink.setAttribute('data-olsop-css', '');
    cssLink.addEventListener('load', function () { cssLink.__loaded = true; });
    cssLink.addEventListener('error', function () { cssLink.__loaded = true; });
    document.head.appendChild(cssLink);
  }

  /* ------------------------------------------------------------ turnstile */
  var turnstileLoader = null;
  var widgetId = null;

  function loadTurnstile() {
    if (turnstileLoader) return turnstileLoader;
    turnstileLoader = new Promise(function (resolve, reject) {
      if (window.turnstile) return resolve();
      var timer = setTimeout(function () {
        turnstileLoader = null;
        reject(new Error('Turnstile load timeout'));
      }, 10000);
      var s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.onload = function () { clearTimeout(timer); resolve(); };
      s.onerror = function () { clearTimeout(timer); turnstileLoader = null; reject(new Error('Turnstile failed')); };
      document.head.appendChild(s);
    });
    return turnstileLoader;
  }

  /* One widget at a time: rendering a second without removing the first leaves
     an orphan that never resolves. */
  function getToken(container) {
    return loadTurnstile().then(function () {
      return new Promise(function (resolve, reject) {
        if (widgetId !== null) {
          try { window.turnstile.remove(widgetId); } catch (e) {}
          widgetId = null;
        }
        widgetId = window.turnstile.render(container, {
          sitekey: CFG.turnstileKey,
          size: 'invisible',
          callback: resolve,
          'error-callback': function (e) { reject(new Error('Turnstile error: ' + e)); },
          'timeout-callback': function () { reject(new Error('Turnstile timeout')); }
        });
        window.turnstile.execute(widgetId);
      });
    });
  }

  /* ----------------------------------------------------------- open/close */
  /* open() must not paint the form before its stylesheet has arrived; after the
     deadline it opens regardless, so a blocked sheet cannot strand it. */
  var cssDeadline = 0;
  function cssPending() {
    if (!cssLink || cssLink.__loaded) return false;
    if (!cssDeadline) cssDeadline = Date.now() + 4000;
    return Date.now() < cssDeadline;
  }

  var lastFocus = null;
  var pendingClose = null;              /* set while a close fade is running */

  /* ---------------------------------------------------------- page lock ---
     Sam, 9 Sep 2026, from a real iPhone: swiping with the popup open scrolled
     the page behind it rather than the modal. Nothing was locking the page.

     `overflow: hidden` on the body is not enough on iOS; taking the body out
     of flow is the part Safari honours, so the class in optin-popup.css sets
     `position: fixed` and we carry the scroll offset across in `top` — without
     that the page jumps to the top the moment the popup opens, and stays there
     when it closes. */
  var lockedAt = 0;

  function lockPage() {
    if (document.body.classList.contains('olsop-locked')) return;
    lockedAt = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = (-lockedAt) + 'px';
    document.body.classList.add('olsop-locked');
  }

  function unlockPage() {
    if (!document.body.classList.contains('olsop-locked')) return;
    document.body.classList.remove('olsop-locked');
    document.body.style.top = '';
    /* 'instant': the page must not animate back to where it already was. */
    try { window.scrollTo({ top: lockedAt, behavior: 'instant' }); }
    catch (e) { window.scrollTo(0, lockedAt); }
  }

  function open() {
    if (cssPending()) { setTimeout(open, 120); return; }
    if (pendingClose) pendingClose();       /* a fade still running ends now */
    try { sessionStorage.setItem(CFG.sessionKey, 'true'); } catch (e) {}
    lastFocus = document.activeElement;
    root.removeAttribute('hidden');
    root.classList.add('is-open');
    lockPage();
    document.addEventListener('keydown', onKey);
    loadTurnstile().catch(function () {});   /* warm it while they pick a goal */
    track('Welcome Popup Viewed', { source: CFG.popupId });
    var first = q('.olsop-step.is-active button');
    if (first) first.focus();
  }

  /* Fade out, then hide. `is-closing` runs the reverse of the open animation
     (optin-popup.css); `hidden` lands on the root's animationend, with a 300ms
     timer as the fallback for a browser that never fires it. Reduced motion
     keeps the instant hide. A second close during the fade is a no-op. */
  function close() {
    if (pendingClose || root.hasAttribute('hidden')) return;
    track('Welcome Popup Closed', { at_step: currentStep() });
    document.removeEventListener('keydown', onKey);
    if (widgetId !== null && window.turnstile) {
      try { window.turnstile.remove(widgetId); } catch (e) {}
      widgetId = null;
    }
    var timer = null;
    function finish() {
      if (!pendingClose) return;
      pendingClose = null;
      clearTimeout(timer);
      root.removeEventListener('animationend', onEnd);
      root.classList.remove('is-open', 'is-closing');
      root.setAttribute('hidden', '');
      /* Here rather than at the top of close(): the page stays locked for the
         whole fade, so it cannot reflow underneath the animation. finish()
         always runs — animationend, the 300ms fallback, or straight away under
         reduced motion — and is guarded against running twice. */
      unlockPage();
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function onEnd(e) { if (e.target === root) finish(); }
    pendingClose = finish;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { finish(); return; }
    root.addEventListener('animationend', onEnd);
    root.classList.add('is-closing');
    timer = setTimeout(finish, 300);
  }

  function onKey(e) { if (e.key === 'Escape') close(); }

  function currentStep() {
    var el = q('.olsop-step.is-active');
    return el ? el.getAttribute('data-step') : '';
  }

  function goToStep(name) {
    qa('.olsop-step').forEach(function (el) {
      el.classList.toggle('is-active', el.getAttribute('data-step') === name);
    });
    var focusable = q('.olsop-step.is-active input, .olsop-step.is-active button');
    if (focusable) focusable.focus();
  }

  /* --------------------------------------------------------------- errors */
  function showError(el, message, suggestion, typedLocal) {
    if (suggestion && typedLocal) {
      /* The worker returns a corrected domain when it spots a typo. Offering
         it as a button beats telling someone their address is wrong. */
      el.innerHTML = '';
      el.appendChild(document.createTextNode('Did you mean '));
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'olsop-error-fix';
      b.textContent = typedLocal + '@' + suggestion;
      b.addEventListener('click', function () {
        emailInput.value = typedLocal + '@' + suggestion;
        el.hidden = true;
        q('[data-olsop-email-btn]').click();
      });
      el.appendChild(b);
      el.appendChild(document.createTextNode('?'));
    } else {
      el.textContent = message || 'Something went wrong. Please try again.';
    }
    el.hidden = false;
  }

  /* ----------------------------------------------------------------- goal */
  qa('.olsop-goal').forEach(function (btn) {
    btn.addEventListener('click', function () {
      state.goal = btn.getAttribute('data-goal');
      q('[data-olsop-goal]').textContent = state.goal;
      track('Welcome Popup Goal Picked', { goal: state.goal });
      goToStep('email');
    });
  });

  q('[data-olsop-back]').addEventListener('click', function () { goToStep('goal'); });

  /* ---------------------------------------------------------------- email */
  var emailInput = q('[data-olsop-email]');
  var emailErr = q('[data-olsop-email-err]');
  emailInput.addEventListener('input', function () { emailErr.hidden = true; });

  q('[data-olsop-email-form]').addEventListener('submit', function (e) {
    e.preventDefault();
    state.email = emailInput.value.trim();
    if (!EMAIL_RE.test(state.email)) {
      showError(emailErr, 'Please enter a valid email address.');
      return;
    }

    var btn = q('[data-olsop-email-btn]');
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Sending…';

    getToken(q('[data-olsop-turnstile-email]'))
      .then(function (token) {
        return fetch(CFG.worker + '/validate-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          /* goal and source are extra on top of what welcome-popup sends, and
             the worker DISCARDS them — /validate-email reads the email and the
             token only. They stay in the body so a worker change can pick them
             up; the goal is actually saved by identify() in the success
             handler below. */
          body: JSON.stringify({
            email: state.email,
            turnstile_token: token,
            goal: state.goal,
            source: CFG.popupId
          })
        });
      })
      .then(function (res) {
        if (res.ok) return res.json().catch(function () { return {}; });
        return res.json().catch(function () { return {}; }).then(function (d) { throw d; });
      })
      .then(function () {
        identify({ signup_goal: state.goal });
        track('Welcome Popup Email Submitted', {
          signup_channel: 'email', discount_code: CFG.emailCode, goal: state.goal
        });
        goToStep('sms');
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.textContent = label;
        track('Welcome Popup Email Submit Failed', { error: String((err && (err.reason || err.message)) || err) });
        showError(
          emailErr,
          (err && (err.error || err.message)) || "That didn't go through. Please try again.",
          err && err.reason === 'typo_domain' ? err.suggestion : null,
          state.email.split('@')[0]
        );
      });
  });

  /* ------------------------------------------------------------------ sms */
  var phoneInput = q('[data-olsop-phone]');
  var phoneErr = q('[data-olsop-phone-err]');

  /* Live 555-000-0000 formatting, caret preserved — retyping from the middle
     of a number is otherwise maddening. Ported from welcome-popup.js. */
  phoneInput.addEventListener('input', function () {
    var el = phoneInput;
    var before = el.value.slice(0, el.selectionStart).replace(/\D/g, '').length;
    var digits = el.value.replace(/\D/g, '').slice(0, 10);
    var out = digits;
    if (digits.length > 6) out = digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
    else if (digits.length > 3) out = digits.slice(0, 3) + '-' + digits.slice(3);
    el.value = out;
    var i = 0, seen = 0;
    while (i < out.length && seen < before) { if (/\d/.test(out[i])) seen++; i++; }
    el.setSelectionRange(i, i);
    phoneErr.hidden = true;
  });

  function toE164(v) {
    var d = (v || '').replace(/\D/g, '');
    if (d.length === 10) return '+1' + d;
    if (d.length === 11 && d[0] === '1') return '+' + d;
    return null;
  }

  q('[data-olsop-sms-form]').addEventListener('submit', function (e) {
    e.preventDefault();
    state.phone = toE164(phoneInput.value);
    if (!state.phone) {
      track('Welcome Popup SMS Submit Failed', { error: 'invalid_format' });
      showError(phoneErr, 'Please enter a valid US phone number.');
      return;
    }

    var btn = q('[data-olsop-sms-btn]');
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Please wait…';

    getToken(q('[data-olsop-turnstile-sms]'))
      .then(function (token) {
        return fetch(CFG.worker + '/welcome-sms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: state.email, phone: state.phone, turnstile_token: token })
        });
      })
      .then(function (res) {
        if (res.ok) return res.json().catch(function () { return {}; });
        return res.json().catch(function () { return {}; }).then(function (d) { throw d; });
      })
      .then(function () {
        try { localStorage.setItem(CFG.submittedKey, 'true'); } catch (e) {}
        state.sms = true;
        track('Welcome Popup SMS Submitted', { signup_channel: 'sms', discount_code: 'dynamic' });
        finish();
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.textContent = label;
        track('Welcome Popup SMS Submit Failed', { error: String((err && (err.reason || err.message)) || err) });
        showError(phoneErr, (err && (err.error || err.message)) || "Couldn't add your number. Check the format and try again.");
      });
  });

  q('[data-olsop-skip]').addEventListener('click', function () {
    try { localStorage.setItem(CFG.submittedKey, 'true'); } catch (e) {}
    state.sms = false;
    track('Welcome Popup SMS Skipped', {});
    finish();
  });

  /* ----------------------------------------------------------------- done */
  function finish() {
    var title = q('[data-olsop-done-title]');
    var body = q('[data-olsop-done-body]');
    var code = q('[data-olsop-code]');

    if (state.sms) {
      title.textContent = 'Check your phone';
      body.textContent = "Your unique 10% off code is on its way — valid for 10 days from when you receive it.";
      /* Nothing to show: the SMS code is generated per customer. */
      code.hidden = true;
    } else {
      title.textContent = 'Check your email';
      body.textContent = 'Your $5 off code is on the way. Happy shopping!';
      code.hidden = false;
    }
    goToStep('done');
  }

  var codeBtn = q('[data-olsop-code]');
  codeBtn.addEventListener('click', function () {
    var value = q('[data-olsop-code-value]').textContent;
    var toast = q('[data-olsop-code-toast]');
    if (!navigator.clipboard || !navigator.clipboard.writeText) return;
    navigator.clipboard.writeText(value).then(function () {
      codeBtn.classList.add('is-copied');
      toast.classList.add('is-shown');
      track('Welcome Popup Code Copied', { code: value });
      setTimeout(function () {
        codeBtn.classList.remove('is-copied');
        toast.classList.remove('is-shown');
      }, 1800);
    });
  });

  /* ---------------------------------------------------------------- close */
  qa('[data-olsop-close]').forEach(function (b) { b.addEventListener('click', close); });
  root.addEventListener('click', function (e) { if (e.target === root) close(); });

  /* --------------------------------------------------------------- launch
     Opens after the delay AND the visitor's first interaction (scroll, touch,
     pointer, key). Before 5 Sep 2026 it opened on the timer alone, and that
     put the popup inside every Lighthouse trace: on a phone its card covers
     the viewport, so Speed Index measured the page as "incomplete" until the
     popup appeared (9-11s on collections and the PDP) and its heading became
     the LCP candidate on text-led pages. A reader who has not touched the
     page in five seconds is still reading; the offer waits for the first
     move. data-open-on="timer" on #olsop-root restores the old behaviour.
     The stylesheet is fetched at the timer, off the critical path, so the
     sheet is warm by the time the popup is allowed to open. */
  if (forced) { loadCss(); open(); }
  else if (root.getAttribute('data-open-on') === 'timer') {
    setTimeout(function () { loadCss(); open(); }, CFG.openDelay);
  } else {
    var elapsed = false, touched = false, launched = false;
    var firstEvents = ['scroll', 'pointerdown', 'touchstart', 'keydown', 'mousemove'];
    function maybeOpen() {
      if (launched || !elapsed || !touched) return;
      launched = true;
      loadCss();
      open();
    }
    function onFirstInteraction() {
      touched = true;
      firstEvents.forEach(function (ev) { window.removeEventListener(ev, onFirstInteraction, true); });
      maybeOpen();
    }
    firstEvents.forEach(function (ev) {
      window.addEventListener(ev, onFirstInteraction, { capture: true, passive: true });
    });
    setTimeout(function () { elapsed = true; loadCss(); maybeOpen(); }, CFG.openDelay);
  }
})();
